import { useModuleStatuses } from '@features/properties';
import { useRoomSettings } from '@features/roomSettings';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useTranslations } from '@lang';
import { ParsedJourneyParams } from '@store/yourJourney/selectors';
import { FC } from 'react';
import { Link } from 'shared-routing';
import { Button } from '@common/components/Button';
import { PaymentTermsBadge } from '@common/components/PaymentTermsBadge';
import { buildCardImageUrl } from '@utils/buildCardImageUrl';
import { BookableListItemType } from '../list.types';
import {
  ActionsColumn,
  Image,
  ImageColumn,
  ItemWrapper,
  PaymentTermsWrapper,
  PerStayIndicator,
  PricePerNight,
  PricingColumn,
  RoomNumberColumn,
  StyledPrice,
  StyledPricePerNight,
} from './BookableListItem.styles';
import { ItemDetails } from './ItemDetails';

type BookableListItemProps = Omit<BookableListItemType, 'id'> & {
  isFetching?: boolean;
  journeyParams?: ParsedJourneyParams;
  triggerValidation?: () => void;
};

export const BookableListItem: FC<BookableListItemProps> = ({
  linkRoute,
  linkParams,
  linkCTA,
  imageUrl,
  title,
  description = '',
  pricePerNight,
  pricePerStay,
  terms,
  numberOfNights,
  numberOfRooms,
  isFetching,
  journeyParams,
  triggerValidation,
  paymentTerms,
}) => {
  const { t } = useTranslations();
  if (linkParams.childAges) {
    linkParams.childAges = JSON.stringify(linkParams.childAges);
  }

  const {
    statuses: { isRoomsEnabled },
  } = useModuleStatuses();
  const { data: roomSettings } = useRoomSettings(isRoomsEnabled);
  const isMultiRoomsEnabled =
    useFeatureIsOn('feature.multi-room.enabled') && roomSettings?.data.multiRoomSearchEnabled;

  const shouldPreventBooking = () => {
    if (!journeyParams) {
      return false;
    }

    const showChildAges = roomSettings?.data?.showChildAges;
    const hasChildren = journeyParams.children > 0;

    if (!showChildAges || !hasChildren) {
      return false;
    }

    const childAges = journeyParams.childAges || {};
    const childAgeEntries = Object.entries(childAges);
    const validChildAges = childAgeEntries.filter(
      ([key, value]) => key.startsWith('child') && typeof value === 'number'
    );

    const shouldPrevent = validChildAges.length < journeyParams.children;
    return shouldPrevent;
  };

  const handleBookingClick = () => {
    if (shouldPreventBooking() && triggerValidation) {
      triggerValidation();
    }
  };

  return (
    <ItemWrapper>
      <ImageColumn>
        <Image alt={title} src={buildCardImageUrl(imageUrl)} loading="lazy" />
      </ImageColumn>
      <ItemDetails title={title} description={description} terms={terms} />
      <PricingColumn>
        <StyledPrice price={pricePerStay} />
        <PerStayIndicator>{t('room.includingVAT')}</PerStayIndicator>
        {numberOfNights && (
          <PricePerNight>
            {t('room.pricePerNight', {
              pricePerNight: <StyledPricePerNight price={pricePerNight} />,
            })}
          </PricePerNight>
        )}
        {isMultiRoomsEnabled && numberOfRooms && (
          <RoomNumberColumn>
            {numberOfRooms}{' '}
            {numberOfRooms <= 1 ? t('common.room').toLowerCase() : t('app.rooms').toLowerCase()}
          </RoomNumberColumn>
        )}
        {paymentTerms && (
          <PaymentTermsWrapper>
            <PaymentTermsBadge paymentTerms={paymentTerms} />
          </PaymentTermsWrapper>
        )}
      </PricingColumn>
      <ActionsColumn>
        {shouldPreventBooking() ? (
          <Button isPrimary isLarge disabled={isFetching} onClick={handleBookingClick}>
            {linkCTA}
          </Button>
        ) : (
          <Link route={linkRoute} params={linkParams}>
            <Button isPrimary isLarge disabled={isFetching}>
              {linkCTA}
            </Button>
          </Link>
        )}
      </ActionsColumn>
    </ItemWrapper>
  );
};
